/**
 * Pure helpers for the Agent Access console — client search/ranking, Google Ads
 * id formatting, and the draft → update-patch diff. No React, no Firebase: the
 * rules that decide what "Save changes" actually sends to the broker live here
 * so they can be unit-tested (see agentAccessHelpers.test.ts).
 *
 * The one server rule everything below mirrors is `applyBoundClient` in
 * functions/src/functions/http/agent-access.ts: when a request carries
 * `boundCustomerId` and no explicit `allowedCustomerIds`, the grant's Google Ads
 * allowlist is RE-DERIVED from that customer's linked `googleAdsCustomerId`
 * (a customer with no link ⇒ an empty list ⇒ no Ads access for a bound grant).
 * An explicit `allowedCustomerIds` array always wins.
 */

export interface ClientOpt {
  id: string;
  name: string;
  company?: string;
  email?: string;
  /** Linked Google Ads account, digits only, and its cached descriptive name. */
  adsId?: string;
  adsName?: string;
}

/** The slice of a grant (as returned by listAgentGrants) these helpers read. */
export interface GrantLike {
  scope?: string;
  capabilities?: string[];
  boundCustomerId?: string;
  allowedCustomerIds?: string[];
  allowedHostingIds?: string[];
  hostingAllAccounts?: boolean;
}

/** What the owner has TOUCHED on a card. A field that is `undefined` has not
 *  been edited and falls through to the grant's saved value — the draft never
 *  materialises defaults, so "dirty" is simply "differs from saved". */
export interface GrantDraft {
  ttl?: number;
  scope?: string;
  /** Raw text of the "restrict to account IDs" override. */
  accounts?: string;
  caps?: string[];
  client?: string;
  /** Raw text of the hosting-ID allowlist. */
  hostids?: string;
  hostingAll?: boolean;
}

export interface UpdatePatch {
  label?: string;
  scope?: 'none' | 'read' | 'read_propose';
  capabilities?: string[];
  boundCustomerId?: string;
  allowedCustomerIds?: string[];
  allowedHostingIds?: string[];
  hostingAllAccounts?: boolean;
}

export const digitsOnly = (s: any): string => String(s ?? '').replace(/\D+/g, '');

/** "4608465215" → "460-846-5215" (how Google shows account ids). Anything that
 *  isn't 10 digits is returned as bare digits rather than guessed at. */
export function formatAdsId(cid: any): string {
  const d = digitsOnly(cid);
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : d;
}

/** Split a pasted list ("123, 456  789") into trimmed, de-duplicated tokens.
 *  `digits` strips each token to digits only (Google Ads ids, which people paste
 *  with dashes); hosting ids are alphanumeric and are left alone. */
export function parseIdList(raw: string | undefined, opts?: { digits?: boolean }): string[] {
  const out: string[] = [];
  for (const t of String(raw ?? '').split(/[\s,;]+/)) {
    const v = opts?.digits ? digitsOnly(t) : t.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

/** Order-insensitive, duplicate-insensitive list equality. */
export function sameSet(a: readonly string[] = [], b: readonly string[] = []): boolean {
  const x = new Set(a), y = new Set(b);
  if (x.size !== y.size) return false;
  return Array.from(x).every((v) => y.has(v));
}

const fold = (s: string): string => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Filter + rank clients for the autocomplete. Every whitespace-separated token
 *  must match somewhere in name / company / email / Ads account name; a token
 *  that looks like an id (3+ digits, dashes allowed) also matches the linked
 *  Ads account number. Ranking: name starts with the query, then a word in the
 *  name does, then a name substring, then everything else (company/email/Ads);
 *  ties alphabetical. `total` is the pre-limit match count so the UI can say
 *  "+N more". */
export function rankClients(clients: ClientOpt[], query: string, limit = 50): { items: ClientOpt[]; total: number } {
  const q = fold(query).trim();
  const tokens = q.split(/\s+/).filter(Boolean);
  const scored: { c: ClientOpt; score: number; name: string }[] = [];
  for (const c of clients) {
    const name = fold(c.name);
    const hay = fold([c.name, c.company, c.email, c.adsName].filter(Boolean).join(' '));
    const ads = digitsOnly(c.adsId);
    let ok = true;
    for (const t of tokens) {
      const td = digitsOnly(t);
      const looksLikeId = td.length >= 3 && /^[\d-]+$/.test(t);
      if (hay.includes(t) || (looksLikeId && ads.includes(td))) continue;
      ok = false;
      break;
    }
    if (!ok) continue;
    let score = 0;
    if (q) {
      score = name.startsWith(q) ? 0
        : name.split(/[\s\-_.&,()/]+/).some((w) => w.startsWith(q)) ? 1
        : name.includes(q) ? 2 : 3;
    }
    scored.push({ c, score, name });
  }
  scored.sort((a, b) => a.score - b.score || a.name.localeCompare(b.name, undefined, { numeric: true }));
  return { items: scored.slice(0, limit).map((s) => s.c), total: scored.length };
}

/** The Google Ads accounts a grant would be able to reach given its saved state
 *  plus whatever the owner has staged — i.e. what the card should SHOW. Mirrors
 *  the server (see header): an explicit override wins (blank = follow the
 *  customer's link), a client change re-derives, otherwise the saved list.
 *  `rederive` is for grants that are about to be (re-)approved — approval
 *  always re-derives from the customer's current link — as opposed to an
 *  already-approved grant, whose saved list is the truth until edited. */
export function effectiveAdsAccounts(
  g: GrantLike, d: GrantDraft | undefined, linkedAdsOf: (clientId: string) => string, rederive: boolean,
): string[] {
  const curClient = g.boundCustomerId || '';
  const client = d?.client !== undefined ? d.client : curClient;
  const touched = d?.accounts !== undefined ? parseIdList(d.accounts, { digits: true }) : null;
  if (touched && touched.length) return touched;                    // an explicit list always wins
  const derive = (cid: string): string[] => { const l = cid ? linkedAdsOf(cid) : ''; return l ? [l] : []; };
  if (client) {
    // A blank override, a client change, or a first approval all re-derive from the link.
    return (touched || rederive || client !== curClient) ? derive(client) : (g.allowedCustomerIds || []);
  }
  // No client bound, so there is nothing to derive from: a saved list stands,
  // unless the owner just unbound the client (the server then locks Ads) or
  // blanked the field.
  return (touched || client !== curClient) ? [] : (g.allowedCustomerIds || []);
}

/** The `update` request body for a card's staged edits — ONLY what differs from
 *  the saved grant ({} = nothing to save). Note what it deliberately does NOT
 *  do: it never sends `boundCustomerId` unless the client changed (or a blank
 *  override asks for a re-derive), because the server re-derives the Ads
 *  allowlist whenever that field is present and would otherwise clobber a
 *  deliberate account list. */
export function buildUpdatePatch(
  g: GrantLike, d: GrantDraft | undefined, linkedAdsOf: (clientId: string) => string,
): UpdatePatch {
  const p: UpdatePatch = {};
  if (!d) return p;
  const curClient = g.boundCustomerId || '';
  const effClient = d.client !== undefined ? d.client : curClient;
  const clientChanged = d.client !== undefined && d.client !== curClient;

  if (clientChanged) p.boundCustomerId = d.client;
  if (d.scope !== undefined && d.scope !== (g.scope ?? 'read')) p.scope = d.scope as UpdatePatch['scope'];
  if (d.caps !== undefined && !sameSet(d.caps, g.capabilities || [])) p.capabilities = d.caps;

  if (d.accounts !== undefined) {
    const list = parseIdList(d.accounts, { digits: true });
    const saved = g.allowedCustomerIds || [];
    if (list.length === 0 && effClient) {
      // Blank override on a bound grant = "follow the customer's linked
      // account": have the server re-derive — but only if that would change
      // something, so an already-in-sync card doesn't read as dirty.
      const target = linkedAdsOf(effClient) ? [linkedAdsOf(effClient)] : [];
      if (!sameSet(target, saved) && !clientChanged) p.boundCustomerId = effClient;
    } else if (clientChanged || !sameSet(list, saved)) {
      p.allowedCustomerIds = list;
    }
  }

  if (d.hostids !== undefined) {
    const list = parseIdList(d.hostids);
    if (!sameSet(list, g.allowedHostingIds || [])) p.allowedHostingIds = list;
  }
  if (d.hostingAll !== undefined && d.hostingAll !== (g.hostingAllAccounts === true)) p.hostingAllAccounts = d.hostingAll;
  return p;
}

export interface DescribeCtx {
  clientName: (id: string) => string;
  capLabel: (id: string) => string;
  scopeLabel: (scope: string) => string;
  adsLabel: (cid: string) => string;
}

/** Human-readable lines for the "unsaved changes" bar — derived from the PATCH
 *  (not the draft), so what the bar promises is exactly what Save will send. */
export function describePatch(g: GrantLike, p: UpdatePatch, ctx: DescribeCtx): string[] {
  const out: string[] = [];
  if (p.boundCustomerId !== undefined) {
    if (p.boundCustomerId !== (g.boundCustomerId || '')) {
      out.push(p.boundCustomerId ? `client → ${ctx.clientName(p.boundCustomerId)}` : 'client → none');
    } else {
      out.push('Google Ads account → match the customer’s linked account');
    }
  }
  if (p.scope !== undefined) out.push(`Google Ads: ${ctx.scopeLabel(g.scope ?? 'read')} → ${ctx.scopeLabel(p.scope)}`);
  if (p.allowedCustomerIds !== undefined) {
    out.push(p.allowedCustomerIds.length ? `Ads accounts → ${p.allowedCustomerIds.map(ctx.adsLabel).join(', ')}` : 'Ads accounts → none');
  }
  if (p.capabilities !== undefined) {
    const cur = new Set(g.capabilities || []);
    const next = new Set(p.capabilities);
    for (const c of p.capabilities) if (!cur.has(c)) out.push(`+ ${ctx.capLabel(c)}`);
    for (const c of g.capabilities || []) if (!next.has(c)) out.push(`− ${ctx.capLabel(c)}`);
  }
  if (p.allowedHostingIds !== undefined) out.push(`hosting IDs → ${p.allowedHostingIds.length ? p.allowedHostingIds.join(', ') : 'none'}`);
  if (p.hostingAllAccounts !== undefined) out.push(`full hosting fleet → ${p.hostingAllAccounts ? 'on' : 'off'}`);
  return out;
}
